import { c as _c } from "react/compiler-runtime";
import { FC, SVGProps } from 'react';
import { niceWholePctBounds, pallet, useDebouncedSize } from './charts';
import { clamp, flatMap, reverse } from 'remeda';
import { ConditionalsProps, NameValue } from './conditionals';
import { scaleLinear } from 'd3-scale';
import { O } from 'ts-toolbelt';
import { GSpace, Svg } from '../svg/Svg';
import { XAxisLabelsG } from './Labels';
import { notNull } from '../../type';
import { ChildrenProps, StyleProps } from '../../ui';
import { fmtWholePct } from '../../format';
import { Rnge } from '../../range';
import { G } from '../svg/G';
import { useSafeMantineTheme } from '@mantine/core';
const {
  abs
} = Math;
export const Conditionals = t0 => {
  const $ = _c(20);
  const {
    chart,
    className,
    onHover
  } = t0;
  const {
    source,
    question
  } = chart;
  const {
    width,
    ref
  } = useDebouncedSize();
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== chart || $[1] !== className || $[2] !== onHover || $[3] !== question || $[4] !== ref || $[5] !== width) {
    const layout = causesLayout({
      conditionals: [],
      width,
      ...chart
    });
    t1 = ref;
    t2 = `flex flex-col gap-0 py-2 pr-2 ${className}`;
    const t5 = question?.long ?? "";
    if ($[10] !== t5) {
      t3 = <h2>{t5}</h2>;
      $[10] = t5;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t4 = layout?.conditionals.map((c, i) => <ConditionRow key={i} conditional={c} layout={layout} question={question} onHover={onHover} />);
    $[0] = chart;
    $[1] = className;
    $[2] = onHover;
    $[3] = question;
    $[4] = ref;
    $[5] = width;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[12] !== source) {
    t5 = source && <div className="text-xs italic text-gray-400">{source}</div>;
    $[12] = source;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = <div ref={t1} className={t2}>{t3}{t4}{t5}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
const minBarWidth = 200;
const causesLayout = ({
  conditionals,
  question,
  width
}: {
  width: number;
} & O.Required<Pick<ConditionalsProps['chart'], 'conditionals' | 'question'>, 'conditionals'>) => {
  const allValues = flatMap(conditionals ?? [], c => [c.ifFalse?.value, c.ifTrue?.value]).filter(notNull);
  const bounds: [number, number] = niceWholePctBounds(allValues, 1);
  const barWidth = width ? clamp(width / 2 - 120, {
    min: minBarWidth,
    max: 300
  }) : minBarWidth;
  return {
    bounds,
    scale: scaleLinear(bounds, [0, barWidth]),
    likelihoodScale: scaleLinear([0, 100], [0, barWidth - 100]),
    conditionals: conditionals.map((c, i) => ({
      ...c,
      isTop: i == 0
    })),
    question
  };
};
type Layout = ReturnType<typeof causesLayout>;
const barHeight = 30;
type LayoutConditional = Layout['conditionals'][number];
type ConditionRowProps = {
  conditional: LayoutConditional;
  layout: Omit<Layout, 'conditionals'>;
} & Pick<ConditionalsProps['chart'], 'question'> & Pick<ConditionalsProps, 'onHover'> & StyleProps;
const ConditionRow = t0 => {
  const $ = _c(30);
  const {
    className,
    layout,
    conditional,
    onHover
  } = t0;
  const {
    likelihood,
    title,
    isTop
  } = conditional;
  const t1 = `mt-0 flex flex-col items-start md:flex-row ${className} hover:bg-gray-100`;
  let t2;
  if ($[0] !== conditional || $[1] !== onHover) {
    t2 = () => onHover?.(conditional);
    $[0] = conditional;
    $[1] = onHover;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onHover) {
    t3 = () => onHover?.(undefined);
    $[3] = onHover;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `${isTop ? "md:pt-10" : "md:pt-4"} px-2 md:w-1/3 md:min-w-[15em]`;
  let t5;
  if ($[5] !== t4 || $[6] !== title) {
    t5 = <h3 className={t4}>{title}</h3>;
    $[5] = t4;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      padding: 15
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isTop || $[10] !== layout || $[11] !== likelihood) {
    t7 = likelihood && <G y={isTop ? 25 : 0} style={{}}><g><LikelihoodBarOld isTop={isTop} likelihood={likelihood} {...layout} /></g></G>;
    $[9] = isTop;
    $[10] = layout;
    $[11] = likelihood;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== layout.likelihoodScale) {
    t8 = Rnge.width(layout.likelihoodScale.range());
    $[13] = layout.likelihoodScale;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = t8 + 20;
  let t10;
  if ($[15] !== conditional || $[16] !== layout) {
    t10 = <g><ConditionBar {...layout} {...conditional} /></g>;
    $[15] = conditional;
    $[16] = layout;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <G x={t9}>{t10}</G>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t7) {
    t12 = <Svg style={t6}><g>{t7}{t11}</g></Svg>;
    $[21] = t11;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t1 || $[25] !== t12 || $[26] !== t2 || $[27] !== t3 || $[28] !== t5) {
    t13 = <div className={t1} onMouseOver={t2} onMouseOut={t3}>{t5}{t12}</div>;
    $[24] = t1;
    $[25] = t12;
    $[26] = t2;
    $[27] = t3;
    $[28] = t5;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
export const PctBar: FC<Pick<LayoutConditional, 'isTop'> & Pick<Layout, 'scale'> & ChildrenProps & StyleProps & {
  classes?: {
    bgBar?: string;
  };
  location?: 'above' | 'below';
}> = t0 => {
  const $ = _c(27);
  const {
    isTop,
    scale,
    children,
    style,
    classes,
    location: t1
  } = t0;
  const location = t1 === undefined ? "below" : t1;
  let t2;
  if ($[0] !== scale) {
    t2 = scale.range();
    $[0] = scale;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [startX, endX] = t2 as [number, number];
  const t3 = location == "below" ? "column-reverse" : "column";
  let t4;
  if ($[2] !== style || $[3] !== t3) {
    t4 = {
      display: "flex",
      paddingLeft: 10,
      flexDirection: t3,
      ...style
    };
    $[2] = style;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isTop || $[6] !== scale) {
    t5 = isTop && <g><XAxisLabelsG scale={scale} /></g>;
    $[5] = isTop;
    $[6] = scale;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== location) {
    t6 = location == "below" ? {
      marginBottom: 3
    } : {};
    $[8] = location;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = endX - startX;
  const t8 = `fill-gray-200 ${classes?.bgBar ?? ""}`;
  let t9;
  if ($[10] !== startX || $[11] !== t7 || $[12] !== t8) {
    t9 = <rect id="bg-bar" x={startX} width={t7} height={barHeight} className={t8} />;
    $[10] = startX;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== endX) {
    t10 = <GSpace x={endX} width={15} />;
    $[14] = endX;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== children) {
    t11 = <g>{children}</g>;
    $[16] = children;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t6 || $[21] !== t9) {
    t12 = <g style={t6}>{t9}{t10}{t11}</g>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t6;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t4 || $[25] !== t5) {
    t13 = <G style={t4}>{t5}{t12}</G>;
    $[23] = t12;
    $[24] = t4;
    $[25] = t5;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
};
const segmentLayout = ({
  scale,
  name,
  value: conditionLikelihood,
  edge,
  question
}: NameValue & {
  edge: LabelLayoutEdge;
} & Pick<Layout, 'scale' | 'question'>) => {
  const qValue = question?.value ?? 0;
  const qx = scale(qValue);
  const deltaX = scale(conditionLikelihood) - qx;
  const pos = deltaX > 0;
  const widthPx = abs(deltaX);
  const x = pos ? qx : qx - widthPx;
  const bar = {
    x,
    width: widthPx,
    height: barHeight
  };
  const inside = widthPx > 60 ? 'inside' : 'outside';
  const p = 5;
  const label = ({
    'left-outside': {
      x: bar.x - p,
      anchor: 'end'
    },
    'left-inside': {
      x: bar.x + bar.width - p,
      anchor: 'end'
    },
    'right-outside': {
      x: bar.x + bar.width + p,
      anchor: 'start'
    },
    'right-inside': {
      x: bar.x + bar.width - p,
      anchor: 'end'
    }
  } as Record<LabelLayoutEdgeInside, {
    x: number;
    anchor: 'start' | 'end';
  }>)[`${edge}-${inside}`];
  return {
    bar: bar,
    widthPx,
    delta: conditionLikelihood - qValue,
    name,
    edge,
    inside,
    label,
    qx
  };
};
type SegmentLayout = ReturnType<typeof segmentLayout>;
type LabelLayoutEdge = 'left' | 'right';
type LabelLayoutInside = 'inside' | 'outside';
type LabelLayoutEdgeInside = `${LabelLayoutEdge}-${LabelLayoutInside}`;
const ConditionBar = ({
  ifFalse,
  ifTrue,
  isTop,
  scale,
  question
}: LayoutConditional & Pick<Layout, 'scale' | 'question'>) => {
  const qLabel = question?.short ?? '';
  const qValue = question?.value ?? 0;
  const qx = scale(qValue);
  const [start, end] = scale.domain() as [number, number];
  const is100 = start == 0 && end == 100;
  const ifTrueLabelPos = (ifTrue?.value ?? 0) > (ifFalse?.value ?? 0) ? 'right' : 'left';
  const trueLayout = ifTrue ? segmentLayout({
    scale,
    question,
    ...ifTrue,
    edge: ifTrueLabelPos
  }) : null;
  const falseLayout = ifFalse ? segmentLayout({
    scale,
    question,
    ...ifFalse,
    edge: ifTrueLabelPos == 'right' ? 'left' : 'right'
  }) : null;
  return <G style={{
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 40
  }}>
      {isTop && <g style={{
      marginBottom: 5
    }}>
          <Text x={qx} className='label'>
            <tspan className='font-semibold'>{qLabel}</tspan>
            <tspan dy='1.2em' x={qx} className='font-bold'>
              {fmtWholePct(qValue)}
            </tspan>
          </Text>
        </g>}
      <g>
        <PctBar isTop={isTop} scale={scale} classes={{
        bgBar: !is100 ? 'opacity-0' : ''
      }}>
          {trueLayout && <ConditionDelta {...trueLayout} />}
          {falseLayout && <ConditionDelta {...falseLayout} />}
          <line id='question-line' x1={qx} y1={-5} x2={qx} y2={barHeight + 5} className='stroke-gray-700' strokeWidth={2} />
        </PctBar>
      </g>
    </G>;
};
const ConditionDelta = t0 => {
  const $ = _c(22);
  const {
    bar,
    inside,
    name,
    label,
    delta,
    qx,
    edge
  } = t0;
  const {
    colors,
    white
  } = useSafeMantineTheme();
  const fill = delta == 0 ? colors.gray[7] : delta > 0 ? pallet.blue : pallet.red;
  let t1;
  if ($[0] !== bar || $[1] !== fill) {
    t1 = <rect id="ifFalse" {...bar} fill={fill} opacity={1} />;
    $[0] = bar;
    $[1] = fill;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = label.x;
  const t3 = label.anchor;
  const t4 = inside == "inside" ? white : fill;
  const t5 = inside == "inside" ? 0.8 : 1;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      fontSize: "1.2em"
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = delta > 0 ? "+" : "";
  let t8;
  if ($[4] !== delta) {
    t8 = delta.toFixed(0);
    $[4] = delta;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== label.anchor || $[7] !== label.x || $[8] !== t4 || $[9] !== t5 || $[10] !== t7 || $[11] !== t8) {
    t9 = <Text x={t2} y={barHeight / 2} className="font-bold" textAnchor={t3} dominantBaseline="central" fill={t4} opacity={t5} style={t6}>{t7}{t8}%</Text>;
    $[6] = label.anchor;
    $[7] = label.x;
    $[8] = t4;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = qx + 3 * (edge == "left" ? -1 : 1);
  const t11 = edge == "left" ? "end" : "start";
  let t12;
  if ($[13] !== fill || $[14] !== name || $[15] !== t10 || $[16] !== t11) {
    t12 = <Text x={t10} y={barHeight + 20} textAnchor={t11} dominantBaseline={"top" as any} label={name} fill={fill} className="font-semibold" />;
    $[13] = fill;
    $[14] = name;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t1 || $[19] !== t12 || $[20] !== t9) {
    t13 = <>{t1}{t9}{t12}</>;
    $[18] = t1;
    $[19] = t12;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
};

//#endregion

type TextProps = Pick<SVGProps<SVGTextElement>, 'x' | 'y' | 'textAnchor' | 'dominantBaseline' | 'fill' | 'style' | 'opacity'> & {
  label?: string;
  dominantBaseline?: 'central' | 'top' | 'bottom' | 'auto' | 'hanging';
} & StyleProps & ChildrenProps;
const Text = t0 => {
  const $ = _c(12);
  let children;
  let fill;
  let label;
  let p;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      children,
      fill,
      dominantBaseline: t1,
      ...p
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = fill;
    $[3] = label;
    $[4] = p;
    $[5] = t1;
  } else {
    children = $[1];
    fill = $[2];
    label = $[3];
    p = $[4];
    t1 = $[5];
  }
  const dominantBaseline = t1 === undefined ? "top" as any : t1;
  const {
    colors
  } = useSafeMantineTheme();
  const t2 = fill ?? colors.gray[5];
  let t3;
  if ($[6] !== children || $[7] !== dominantBaseline || $[8] !== label || $[9] !== p || $[10] !== t2) {
    t3 = <text {...p} dominantBaseline={dominantBaseline} fill={t2}>{label}{children}</text>;
    $[6] = children;
    $[7] = dominantBaseline;
    $[8] = label;
    $[9] = p;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export const LikelihoodBarOld = t0 => {
  const $ = _c(35);
  const {
    isTop,
    likelihood,
    likelihoodScale,
    color: rawColor
  } = t0;
  const {
    colors
  } = useSafeMantineTheme();
  const color = rawColor ?? colors.blue[6];
  const labelInside = (likelihood ?? 0) > 40;
  let t1;
  if ($[0] !== likelihoodScale) {
    t1 = likelihoodScale.range();
    $[0] = likelihoodScale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [startX, endX] = t1 as [number, number];
  const width = endX - startX;
  const valueX = likelihoodScale(likelihood);
  const likeLabelOutside = isTop && width < 150;
  let t2;
  if ($[2] !== isTop || $[3] !== likeLabelOutside) {
    t2 = () => isTop && !likeLabelOutside ? <tspan>likelihood </tspan> : <></>;
    $[2] = isTop;
    $[3] = likeLabelOutside;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== likelihood) {
    t3 = () => <tspan key={1} className="font-bold">{fmtWholePct(likelihood)}</tspan>;
    $[5] = likelihood;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let labelProps;
  if ($[7] !== labelInside || $[8] !== t2 || $[9] !== t3) {
    labelProps = [t2, t3];
    if (!labelInside) {
      reverse(labelProps);
    }
    $[7] = labelInside;
    $[8] = t2;
    $[9] = t3;
    $[10] = labelProps;
  } else {
    labelProps = $[10];
  }
  let t4;
  if ($[11] !== color || $[12] !== likeLabelOutside) {
    t4 = likeLabelOutside && <g><text className="font-semibold" style={{
        dominantBaseline: "hanging",
        fill: color
      }}>likelihood</text></g>;
    $[11] = color;
    $[12] = likeLabelOutside;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== color || $[15] !== valueX) {
    t5 = <rect id="likelihood-bar" height={barHeight} width={valueX} fill={color} />;
    $[14] = color;
    $[15] = valueX;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = labelInside ? valueX - 5 : valueX + 5;
  const t7 = labelInside ? "end" : "start";
  const t8 = labelInside ? colors.gray[0] : color;
  const t9 = labelInside ? 0.8 : 1;
  let t10;
  if ($[17] !== t9) {
    t10 = {
      opacity: t9
    };
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== labelProps) {
    t11 = labelProps.map(_temp);
    $[19] = labelProps;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t12 = <text x={t6} y={barHeight / 2} dominantBaseline="central" textAnchor={t7} fill={t8} style={t10}>{t11}</text>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== isTop || $[28] !== likelihoodScale || $[29] !== t12 || $[30] !== t5) {
    t13 = <g><PctBar isTop={isTop} scale={likelihoodScale}>{t5}{t12}</PctBar></g>;
    $[27] = isTop;
    $[28] = likelihoodScale;
    $[29] = t12;
    $[30] = t5;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t4) {
    t14 = <G>{t4}{t13}</G>;
    $[32] = t13;
    $[33] = t4;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};
function _temp(L, i) {
  return <L key={i} />;
}